import * as CL from '@design-system/component-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteLoaderData } from 'react-router';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { dsClass } from '../../common/constants/dsClasses.js';
import { ESIASENNUSPALVELU } from '../../common/constants/subscriptionConstants';
import { t } from '../../common/i18n/index.js';
import { scrollTo } from '../../common/utils/browserUtils.js';
import {
  getDeliveryDetails,
  getDeliveryDetailsObj,
  getOrderItems,
  getProductDeliveryTypesInCart,
  hasEmployeePayables,
  showEppDeviceChangeStep,
} from '../../common/utils/checkoutUtils.js';
import { getTopMostElement } from '../../common/utils/domUtils.js';
import { convertStringMapToCommonErrors, getElementsWithErrors } from '../../common/utils/errorUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider';
import { loadDeliveryMethods, resetMoveToNextStep, submitOnlineOrder } from '../../selfservice/actions/index.js';
import { DeliveryAddressType } from '../DeliveryAddress/DeliveryAddress.js';
import { employeeDeviceChangeStep } from '../DeviceCheckout/EmployeeDeviceChange.js';
import { useCheckout } from './EoeCheckout.js';
import { handleCheckoutAnalytics } from './checkoutAnalyticsUtils';
import { cartProductsStep } from './steps/CartProductsStep.js';
import { deliveryDetailsStep } from './steps/DeliveryDetailsStep.js';
import { employeeUserPersonalBillingStep } from './steps/EmployeeUserPersonalBillingStep.js';
import type { CheckoutRootLoaderData } from '../../common/loaders.js';
import type { SubmitOrderProps } from '../../common/types/checkout.js';
import type { CLStepperItem } from '../../common/types/componentLibrary';
import type { DeviceChangeRequest } from '../../common/types/device.js';
import type { CommonError } from '../../common/types/errors.js';
import type { ContactPerson } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';

export type CheckoutStepItem = CLStepperItem & {
  completedName?: string;
};

export interface CheckoutStepsProps {
  rootLoaderId: string;
}

export const EoeCheckoutSteps = (props: CheckoutStepsProps) => {
  const dispatch = useDispatch();
  const { currentStep, setCurrentStep } = useCheckout();

  const { ssoSessionValid, authenticatedUser } = useAuth();

  const { moveToNextStep, cartItems, deliveryDetails, selectedBaId, listOfDeliveryMethods, deviceSubs } = useSelector(
    (state: State) => ({
      moveToNextStep: state.deviceCheckout?.moveToNextStep,
      cartItems: state.deviceCheckout?.cartItems || [],
      listOfDeliveryMethods: state.deviceCheckout?.deliveryMethods?.lisOfDeliveryMethods,
      deliveryDetails: state.deviceCheckout?.deliveryDetails,
      selectedBaId: state.deviceCheckout?.selectedBaId,
      deviceSubs: state.user?.authenticated?.subscriptions?.device || [],
    }),
    deepEqual
  );
  const companyInfo = useSessionData().companyInfo;
  const { models, contacts } = useRouteLoaderData(props.rootLoaderId) as CheckoutRootLoaderData;

  const { activeVoucherCode } = useOnlineModelsContext();

  const productDeliveryTypes = useMemo(() => getProductDeliveryTypesInCart(cartItems, models), [cartItems, models]);

  useEffect(() => {
    if (ssoSessionValid) {
      dispatch(loadDeliveryMethods(productDeliveryTypes));
    }
  }, [dispatch, ssoSessionValid, productDeliveryTypes]);

  const [goNextStepState, setGoNextStepState] = useState<() => void>();
  const [deviceChangeRequest, setDeviceChangeRequest] = useState<DeviceChangeRequest>();
  const [deliveryDetailsObj, setDeliveryDetailsObj] = useState<DeviceCheckoutDeliveryDetailsType>(
    deliveryDetails ?? ({} as DeviceCheckoutDeliveryDetailsType)
  );
  const [contactPerson, setContactPerson] = useState<ContactPerson>();
  const [contactPersonValidationErrors, setContactPersonValidationErrors] = useState<{ [s: string]: string }>({});
  const [approverContactObj, setApproverContactObj] = useState<string>();
  const [approverContactValidationErrors, setApproverContactValidationErrors] = useState<{ [s: string]: string }>({});

  const [errors, setErrors] = useState<CommonError[] | undefined>(undefined);
  const shouldScrollToError = useRef(false);
  const hasPreInstallation = cartItems.some(item => item.commercialProductCodes.includes(ESIASENNUSPALVELU));

  const domRefs = useRef<{ [elementKey: string]: HTMLElement }>({});
  const setRefCallback = (key: string, ref: HTMLElement | null) => {
    if (ref) {
      domRefs.current[key] = ref;
    } else {
      delete domRefs.current[key];
    }
  };

  useEffect(() => {
    if (shouldScrollToError && errors && errors.length > 0) {
      scrollTo(getTopMostElement(getElementsWithErrors(domRefs.current, errors)));
    }
  }, [shouldScrollToError, errors]);

  const onShowStep = (step: number) => {
    switch (step) {
      case 2:
        handleCheckoutAnalytics(cartItems, EcommerceEventTypeV4.ADD_SHIPPING_INFO, models);
        break;
      case 3:
        handleCheckoutAnalytics(cartItems, EcommerceEventTypeV4.ADD_PAYMENT_INFO, models);
        break;
    }
  };

  const goNextStep = () => {
    onShowStep(currentStep + 1);
    if (goNextStepState) {
      goNextStepState();
    }
  };

  const goNextStepFn = useCallback(() => {
    setCurrentStep(currentStep + 1);
  }, [currentStep, setCurrentStep]);

  useEffect(() => {
    setGoNextStepState(() => goNextStepFn);
  }, [goNextStepFn]);

  const setShouldScrollToError = (value: boolean, checkoutErrors?: CommonError[]) => {
    shouldScrollToError.current = value;
    setErrors(checkoutErrors);
  };

  const onDeviceChangeNextStep = (updatedDeviceChangeRequest: DeviceChangeRequest): void => {
    setDeviceChangeRequest(updatedDeviceChangeRequest);
    goNextStep();
  };

  const submitOrder = async (submitOrderProps: SubmitOrderProps) => {
    const orderItems = getOrderItems(cartItems, deviceChangeRequest);

    dispatch(
      submitOnlineOrder(
        orderItems,
        getDeliveryDetails(submitOrderProps, deliveryDetails, authenticatedUser),
        contactPerson,
        Object.keys(contactPersonValidationErrors).length === 0
          ? undefined
          : convertStringMapToCommonErrors(contactPersonValidationErrors),
        companyInfo?.customerType,
        submitOrderProps?.updatedDeliveryDetails?.addressType === DeliveryAddressType.COMPANY_ADDRESS,
        deviceChangeRequest?.replacedSubscriptionBillingAccountId ?? selectedBaId,
        undefined,
        undefined,
        submitOrderProps?.updatedDeliveryDetails?.deliveryMethodType ?? deliveryDetails!.deliveryMethodType,
        approverContactObj,
        Object.keys(approverContactValidationErrors).length === 0
          ? undefined
          : convertStringMapToCommonErrors(approverContactValidationErrors),
        submitOrderProps.personBillingAccountId,
        submitOrderProps.personBillingEmail,
        submitOrderProps.personBillingAddress,
        deviceChangeRequest,
        submitOrderProps.updatedDeliveryDetails?.shipmentType ?? deliveryDetails?.shipmentType,
        activeVoucherCode || undefined
      )
    );
    setShouldScrollToError(true);
  };

  useEffect(() => {
    if (moveToNextStep) {
      goNextStep();
      dispatch(resetMoveToNextStep());
    }
  }, [dispatch, moveToNextStep]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(resetMoveToNextStep());
  }, [dispatch]);

  useEffect(() => {
    if (listOfDeliveryMethods) {
      setDeliveryDetailsObj(
        getDeliveryDetailsObj(listOfDeliveryMethods, companyInfo, authenticatedUser, deliveryDetails)
      );
    }
  }, [cartItems, listOfDeliveryMethods, deliveryDetails, companyInfo, authenticatedUser]);

  const stepContents = [
    cartProductsStep(
      {
        shouldScrollToError: shouldScrollToError.current,
        setShouldScrollToError: setShouldScrollToError,
        setDeviceChangeRequest: setDeviceChangeRequest,
        deliveryDetails: deliveryDetailsObj,
        user: authenticatedUser,
      },
      cartItems
    ),

    // Step 2 for Device Change Request (employee)
    showEppDeviceChangeStep(cartItems, authenticatedUser) &&
      authenticatedUser &&
      employeeDeviceChangeStep(
        {
          user: authenticatedUser,
          onNextClick: onDeviceChangeNextStep,
          deviceSubs: deviceSubs,
        },
        deviceChangeRequest
      ),
    deliveryDetailsStep({
      deliveryDetails: deliveryDetailsObj,
      setShouldScrollToError: setShouldScrollToError,
      contactPerson: contactPerson,
      setContactPerson: setContactPerson,
      contactPersonValidationErrors: contactPersonValidationErrors,
      setContactPersonValidationErrors: setContactPersonValidationErrors,
      approverContact: approverContactObj,
      setApproverContact: setApproverContactObj,
      approverContactValidationErrors: approverContactValidationErrors,
      setApproverContactValidationErrors: setApproverContactValidationErrors,
      shouldScrollToError: shouldScrollToError.current,
      deviceChangeRequest: deviceChangeRequest,
      setDeliveryDetailsObj,
      submitOrder: submitOrder,
      setRefCallback: setRefCallback,
      hasPreInstallation: hasPreInstallation,
      contacts: contacts?.contacts,
    }),

    // admin users are not allowed to order OmaLaiteLasku, so even if a company has enabled OmaLaiteLasku and
    // device amount is greater than corporate share set in EPP solution settings,
    // the order will proceed without OmaLaiteLasku and full amount will be charged on corporate BA
    // (employee)
    hasEmployeePayables(cartItems, deviceChangeRequest) &&
      employeeUserPersonalBillingStep({
        deviceChangeRequest: deviceChangeRequest,
        submitOrder: submitOrder,
        deliveryDetails: deliveryDetailsObj,
        contacts: contacts?.contacts,
      }),
  ].filter(Boolean) as CheckoutStepItem[];

  const stepsWithCompleteAndClickHandler = stepContents.map((step, idx) => {
    if (idx < currentStep) {
      step.completed = true;
      step.onClick = () => setCurrentStep(idx);
      step.name = step.completedName ? step.completedName : step.name;
    } else {
      step.completed = false;
      step.onClick = undefined;
    }
    return step;
  });

  if (cartItems.length === 0 || ssoSessionValid === undefined) {
    return <CL.LoadingSpinner size="l" />;
  }

  return (
    <div className="of-checkout-steps">
      <h2>{t.IH3X('Checkout')}</h2>
      <div className={dsClass.RESET}>
        <CL.Stepper
          steps={stepsWithCompleteAndClickHandler}
          activeStep={stepsWithCompleteAndClickHandler[currentStep].id}
        />
      </div>
    </div>
  );
};
